<script lang="ts">
  /**
   * Welche der vier Reisearten gilt.
   *
   * ## Warum es sie gibt
   *
   * Mit den zwei Achsen (Antrieb, Übernachtung) sieht dieselbe Oberfläche je
   * Reise verschieden aus: Bei Verbrenner fehlt der Reiter „Laden", bei
   * Unterkunft schrumpft „Schlafen" auf eine Kategorie. Wer zwei Reisen
   * nebeneinander hat, muss ohne Nachdenken wissen, in welcher er ist —
   * sonst sucht er einen Reiter, den es in dieser Reise nicht gibt.
   *
   * ## Warum kein Farbchip
   *
   * Vier bunte Marken in einer Reiseliste sähen aus wie vier Warnungen. Die
   * Marke ist deshalb neutral hinterlegt; die Aussage tragen **zwei Zeichen
   * nebeneinander**, eines je Achse. Das ist zugleich die Antwort auf die
   * Farbfehlsicht: Zwei Formen bleiben zwei Formen.
   *
   * `knapp` zeigt nur die Zeichen (Reiseliste, Kartenkachel), sonst stehen die
   * Wörter dabei (Planer-Kopf, Einstellungen). Immer **beide** Achsen, auch
   * wenn nur eine ungewöhnlich ist: „Verbrenner" allein lässt offen, wo
   * geschlafen wird, und genau daran hängt die halbe Oberfläche.
   *
   * ## Anders als in der Vorlage
   *
   * Die Zeichenkomponente wird nicht als Prop hereingereicht (in der
   * React-Fassung `Zeichen={Zeichen}`, weil das Muster-Paket den Zeichensatz
   * nicht kennt). Hier liegt beides im selben Projekt, also wird direkt
   * importiert. Die Achsenwerte kommen aus `lib/reiseart.ts` — dort steht
   * ohnehin, was `elektro` und `unterkunft` bedeuten, und zwei Wahrheiten über
   * dieselben zwei Achsen wären eine zu viel.
   */
  import Zeichen from '../Zeichen.svelte';
  import {
    ANTRIEB_VORGABE,
    UEBERNACHTUNG_VORGABE,
    type Antrieb,
    type Uebernachtung
  } from '../../lib/reiseart';
  import { ANTRIEB_MARKE, NACHT_MARKE, reiseartBeschriftung } from './muster';

  interface Props {
    antrieb?: Antrieb;
    uebernachtung?: Uebernachtung;
    /** Nur die Zeichen, ohne Wörter — für Listen und Kartenkacheln. */
    knapp?: boolean;
  }
  let {
    antrieb = ANTRIEB_VORGABE,
    uebernachtung = UEBERNACHTUNG_VORGABE,
    knapp = false
  }: Props = $props();

  const teile = $derived([ANTRIEB_MARKE[antrieb], NACHT_MARKE[uebernachtung]]);
  const beschriftung = $derived(reiseartBeschriftung(antrieb, uebernachtung));
</script>

<span class="marke" class:knapp title={beschriftung} aria-label={beschriftung}>
  {#each teile as teil (teil.zeichen)}
    <span class="teil" style:color={teil.farbe}>
      <Zeichen name={teil.zeichen} groesse={14} />
      {#if !knapp}<span class="wort">{teil.wort}</span>{/if}
    </span>
  {/each}
</span>

<style>
  .marke {
    display: inline-flex;
    align-items: center;
    gap: var(--raum-5);
    font-family: var(--schrift);
    font-size: var(--fs-mikro);
    line-height: 1.4;
    padding: 2px 9px;
    border-radius: var(--radius-rund);
    /* Neutral hinterlegt — die Farbe steckt im Zeichen, nicht in der Fläche. */
    background: var(--art-flaeche);
    white-space: nowrap;
  }
  .marke.knapp {
    gap: var(--raum-3);
    padding: 2px 6px;
  }

  .teil {
    display: inline-flex;
    align-items: center;
    gap: var(--raum-2);
  }

  /* Das Wort steht im Textgrau und nicht in der Achsenfarbe: Zwei farbige
     Wörter nebeneinander lesen sich wie zwei Warnungen. Die Farbe trägt das
     Zeichen. */
  .wort {
    color: var(--text-dim);
  }
</style>
